<template>
  <PublicPageStandardResponsive
    ref="responsive"
    v-if="publicPageData.layoutSize === PublicPageLayoutSize.RESPONSIVE"
    :isPreview="isPreview"
    :kiwiIframeURL="kiwiIframeURL"
    :publicPageData="publicPageData"
    :publicPageTitle="publicPageTitle"
    :projectUserName="projectUserName"
    :viewLayoutRequest="viewLayoutRequest"
    :commentOK="commentOK"
  />
  <PublicPageStandardFixedSize 
    ref="fixedSize"
    v-else 
    :isPreview="isPreview" 
    :kiwiIframeURL="kiwiIframeURL"
    :publicPageData="publicPageData"
    :publicPageTitle="publicPageTitle"
    :projectUserName="projectUserName"
    :viewLayoutRequest="viewLayoutRequest"
    :commentOK="commentOK"
  />
</template>


<script lang="ts">
import { Options} from 'vue-class-component';
import { Ref } from 'vue-property-decorator';
import RootVue from "@/RootVue.vue";
import OnUrlChangeVue from '@/OnUrlChangeVue';
import CommentWriteForm from "@/interface/api/CommentWriteForm";
import PublicPageStandardFixedSize from "@/pages/publicPage/PublicPageStandardFixedSize.vue";
import PublicPageStandardResponsive from "@/pages/publicPage/PublicPageStandardResponsive.vue";
import PublicPageVue from '@/pages/publicPage/_interface/PublicPageVue';
import PublicPageResp from '@/interface/api/PublicPageResp';
import { FMCommentFromCreator, FMCommentLoadConditionChanged, FMCommentWriteConditionChanged, FMInitialized, FMSize, FMSetLayout } from '@/interface/kiwi/ForkstyleMessage';
import { PublicPageLayoutSize } from '@/types/PublicPageLayoutSize';
import CommentManager from '@/logic/CommentManager';
import CommentResp from '@/interface/api/CommentResp';
import { RouteLocationNormalized } from 'vue-router';
import PublicPageStandardVue from '@/pages/publicPage/_interface/PublicPageStandardVue';

@Options({ 
  components: {
    PublicPageStandardFixedSize,
    PublicPageStandardResponsive,
  },
  props:{
    publicPageData:{
      type:Object
    },
    isPreview:{
      type:Boolean,
      default:false,
    },
    kiwiIframeURL:{
      type:String
    },
    publicPageTitle:{
      type:String
    },
    projectUserName:{
      type:String
    },
    viewLayoutRequest:{
      type:Object
    }
  }
})
export default class extends OnUrlChangeVue implements PublicPageVue  {
  // @ts-expect-error
  $root: RootVue;
  readonly publicPageData!: PublicPageResp;
  readonly isPreview!: boolean;
  readonly publicPageTitle!: string;
  readonly projectUserName!: string;
  readonly viewLayoutRequest!: null|FMSetLayout;
  readonly kiwiIframeURL!: string;
  
  //mounted前のlifesycleだと初期化されていないので使用できない点に注意
  @Ref() readonly responsive = null as any as PublicPageStandardResponsive;
  @Ref() readonly fixedSize = null as any as PublicPageStandardFixedSize;

  PublicPageLayoutSize = PublicPageLayoutSize;
  
  //createdで初期化する
  commentManager = null as any as CommentManager;
  
  commentInputCondition = null as string|null;
  inputConditionStartTime = null as null|number;
  
  // プロジェクトのいいねを押す
  onLikeButton() {
    if (this.publicPageData.hasLoginUserGood) {
      // すでに いいね していたら いいね を外す。
      this.deleteLikes();
    } else {
      this.addLikeProject();
    }
  }

  // プロジェクトにいいねする
  addLikeProject() {
    this.$root.restAccess
      .putLikesPublicPage(this.projectUserName, this.publicPageTitle)
      .then((res: any) => {
        this.publicPageData.goodCount = res;
        this.publicPageData.hasLoginUserGood = true;
        (document as any).activeElement.blur();
      });
  }

  // いいねを外す
  deleteLikes() {
    this.$root.restAccess
      .deleteLikesPublicPage(this.projectUserName, this.publicPageTitle)
      .then((res: any) => {
        this.publicPageData.goodCount = res;
        this.publicPageData.hasLoginUserGood = false;
        (document as any).activeElement.blur();
      });
  }

  getChildVueComponent():PublicPageStandardVue{
    switch(this.publicPageData.layoutSize){
      case PublicPageLayoutSize.RESPONSIVE:
        return this.responsive;
      case PublicPageLayoutSize.FIXED_SIZE:
      case PublicPageLayoutSize.FIXED_DETECT:
        return this.fixedSize;
      default:
        throw "unexpected layoutSize"
    }
  }

  public onViewNotFound(){
    this.getChildVueComponent().onViewNotFound();
  }
  public clearIframeSize(){
    this.getChildVueComponent().clearIframeSize();
  }

  public onViewSizeChanged(data: FMSize){
    this.getChildVueComponent().onViewSizeChanged(data);
  }

  public onCommentFromCreator(data: FMCommentFromCreator){
    this.addCommentToBlock({
      user:this.publicPageData.user,
      comment:data.message
    } as any as CommentResp)
  }
  public onCommentWriteConditionChanged(data: FMCommentWriteConditionChanged){
    this.inputConditionStartTime = data.startTime
    this.commentInputCondition = data.condition
  }
  public onCommentLoadConditionChanged(data: FMCommentLoadConditionChanged){
    this.commentManager.switchCondition(data.condition, data.startTime);
  }
  public onViewInitialized(data:FMInitialized){
    this.inputConditionStartTime = data.startTime
    if(!this.isPreview){
      this.commentManager.switchCondition("", data.startTime);
    }
    this.getChildVueComponent().onViewInitialized(data)
  }
  
  public getIframe():HTMLIFrameElement{
    return this.getChildVueComponent().getIframe();
  }

  commentOK(commentText:string) {
    return this.$root.restAccess
      .postCommentPublicPage({
        publicPageId: this.publicPageData.id,
        projectId: this.publicPageData.baseProjectId,
        comment: commentText,
        condition: this.commentInputCondition,
        sceneMilliSecond: this.getCommentTime()
      } as CommentWriteForm)
      .then(() => {
        this.addCommentToBlock({
          user:this.$root.account.info,
          comment:commentText
        } as any as CommentResp)
      })
  }

  addLoadedComment(comment:CommentResp){
    this.addCommentToBlock(comment)
  }
  
  addCommentToBlock(comment:CommentResp){
    const child = this.getChildVueComponent();
    //projectページ->他ページと画面遷移する瞬間に、コメント表示が呼び出されると、getChildVueComponent()がnullになる。
    if(!child){
      if (process.env.VUE_APP_MODE === "development")console.log("公開ページ破棄後にコメント追加が呼び出されました", comment);
      return;
    }

    child.addComment(comment)
  }
  
  getCommentTime(){
    //まだView側より開始していない場合
    if(this.inputConditionStartTime === null){
      return 0;
    }else{
      return (new Date() as any) - (this.inputConditionStartTime as any);
    }
  }

  created(){
    this.commentManager = new CommentManager(this.publicPageData.baseProjectId, this.publicPageData.id, this.addLoadedComment)
  }

  //継承元から呼び出される
  onUrlChanged(_newVal: RouteLocationNormalized, _oldVal: RouteLocationNormalized) {
  }

  beforeUnmount(){
    //先に読み込んでおいたコメントが他のページに移動しても読み込まれたりコールバックされるのを防ぐ
    this.commentManager.destroy();
  }
}
</script>

<style lang="scss" scoped>
</style>
